"use client";

/**
 * General inquiry form on /contact.
 *
 * Visitor fills out email + inquiry type + subject + body. We POST
 * to /api/waitlist with the structured fields packed into `notes`,
 * tagged with `source: "contact-{inquiry}"` so the staff dashboard
 * can route after the fact (no real backend forwarding yet).
 *
 * Inquiry types map to internal inboxes (sales@/support@/etc.) and
 * the success state shows the visitor where their message will be
 * routed. The "route" string is the email address only — the actual
 * routing happens by us reading the table; we don't auto-forward
 * because nobody's wiring up SES/Postmark for outbound until later.
 */

import * as React from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const EMAIL_SHAPE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

interface InquiryType {
  value: string;
  label: string;
  /** Email address the message should logically route to. Surfaced in
   *  the success state so the visitor knows what to expect. */
  route: string;
}

const INQUIRY_TYPES: InquiryType[] = [
  { value: "general", label: "General", route: "hello@pcnshark.com" },
  { value: "sales", label: "Sales", route: "sales@pcnshark.com" },
  {
    value: "enterprise",
    label: "Enterprise / security review",
    route: "enterprise@pcnshark.com",
  },
  { value: "support", label: "Support", route: "support@pcnshark.com" },
  { value: "press", label: "Press / partnerships", route: "press@pcnshark.com" },
  { value: "legal", label: "Legal", route: "legal@pcnshark.com" },
];

interface FormState {
  email: string;
  inquiry: string;
  subject: string;
  body: string;
}

export function ContactForm() {
  const [v, setV] = React.useState<FormState>({
    email: "",
    inquiry: "general",
    subject: "",
    body: "",
  });
  const [stage, setStage] = React.useState<"idle" | "loading" | "done">(
    "idle",
  );
  const [error, setError] = React.useState<string | null>(null);

  const route =
    INQUIRY_TYPES.find((t) => t.value === v.inquiry)?.route ??
    INQUIRY_TYPES[0].route;

  function set<K extends keyof FormState>(k: K) {
    return (
      e: React.ChangeEvent<
        HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
      >,
    ) => setV((s) => ({ ...s, [k]: e.target.value }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const email = v.email.trim().toLowerCase();
    if (!email) {
      setError("Email is required.");
      return;
    }
    if (!EMAIL_SHAPE.test(email)) {
      setError("Please enter a valid email.");
      return;
    }
    if (!v.body.trim()) {
      setError("Please tell us what you need.");
      return;
    }

    setStage("loading");

    // Contact submissions live in their own table (migration
    // 20260504000002), with a separate /api/contact endpoint. Every
    // submission becomes its own row — there's no email-level
    // dedupe like the waitlist endpoint has, because contact is a
    // ledger of conversations rather than a list of identities.
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          inquiry_type: v.inquiry,
          subject: v.subject.trim() || null,
          message: v.body.trim(),
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          detail?: string;
        };
        setError(body.detail ?? "Something went wrong. Please try again.");
        setStage("idle");
        return;
      }
      setStage("done");
    } catch (err) {
      setError(
        (err as Error).message ?? "Couldn't reach the server. Try again.",
      );
      setStage("idle");
    }
  }

  if (stage === "done") {
    return (
      <div
        className="border rule bg-white p-9"
        role="status"
        aria-live="polite"
      >
        <div className="flex items-start gap-4">
          <span
            aria-hidden
            className="mt-1 inline-flex items-center justify-center w-9 h-9 rounded-full text-white text-[15px]"
            style={{ background: "var(--shark-good)" }}
          >
            ✓
          </span>
          <div>
            <h3 className="font-display font-semibold text-[22px] tracking-tight">
              Sent. We read everything.
            </h3>
            <p className="mt-2 text-[14px] ink-2 leading-relaxed max-w-[58ch]">
              Routed to <span className="font-mono">{route}</span>. We&apos;ll
              review your inquiry and respond from the appropriate inbox to{" "}
              <span className="font-mono">{v.email.trim().toLowerCase()}</span>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="border rule bg-white p-7 lg:p-9">
      <div className="grid gap-4">
        <Field label="Your email" required>
          <div className="form-cell">
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              value={v.email}
              onChange={set("email")}
              placeholder="you@somewhere.com"
              required
            />
          </div>
        </Field>

        <Field label="Inquiry type">
          <div className="form-cell select-wrap">
            <select value={v.inquiry} onChange={set("inquiry")}>
              {INQUIRY_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            <span className="select-caret" aria-hidden>
              ▾
            </span>
          </div>
        </Field>

        <Field label="Subject">
          <div className="form-cell">
            <input
              type="text"
              value={v.subject}
              onChange={set("subject")}
              placeholder="One line — what's this about?"
            />
          </div>
        </Field>

        <Field label="Message" required>
          <div className="form-cell">
            <textarea
              rows={6}
              value={v.body}
              onChange={set("body")}
              placeholder="The shorter the better — say what you need."
            />
          </div>
        </Field>
      </div>

      {error && (
        <p
          className="mt-4 font-mono text-[11px]"
          style={{ color: "var(--shark-bad)" }}
          role="alert"
        >
          {error}
        </p>
      )}

      <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
        <div className="font-mono text-[11px] ink-3 max-w-[44ch] leading-relaxed">
          We use your information to respond to your inquiry and communicate
          about the product. We do not sell your data. See our{" "}
          <a
            href="/privacy"
            className="ulink hover:text-[color:var(--ink)]"
          >
            Privacy Policy
          </a>
          .
        </div>
        <button
          type="submit"
          disabled={stage === "loading"}
          className={cn(
            "px-5 py-2.5 bg-[color:var(--ink)] text-white text-[14px] font-medium",
            "hover:bg-black transition inline-flex items-center gap-2",
            "disabled:opacity-60 disabled:cursor-not-allowed",
          )}
        >
          {stage === "loading" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              <span>Sending…</span>
            </>
          ) : (
            <>
              <span>Send message</span>
              <span aria-hidden>→</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="font-mono text-[10px] uppercase tracking-wider ink-3">
          {label}
        </span>
        {required && (
          <span className="font-mono text-[10px] uppercase tracking-wider ink-3">
            required
          </span>
        )}
      </div>
      {children}
    </label>
  );
}
